import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { Box, Typography, TextField, Button, Avatar, InputAdornment } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { COLORS_ADMIN_PAGE, COLORS_CONFIG_PAGE, StyledSidebar } from '../../conf/constants';
import { CONTRACTOR_SIDEBAR_CONSTANTS } from '../constants/contractorSidebar';
import { ContractorSidebarProps } from '../types/contractor.types';

interface ExtendedContractorSidebarProps extends ContractorSidebarProps {
  onContractorsPerPageChange: (contractorsPerPage: number) => void;
}

const ContractorSidebar = forwardRef<HTMLDivElement, ExtendedContractorSidebarProps>(({
  contractors,
  selectedContractor,
  searchTerm,
  currentPage,
  totalContractors,
  contractorsPerPage,
  sidebarWidth,
  onContractorSelect,
  onSearchChange,
  onPageChange,
  onContractorsPerPageChange,
}, ref) => {
  const contractorListRef = useRef<HTMLDivElement>(null);
  const [calculatedContractorsPerPage, setCalculatedContractorsPerPage] = useState(contractorsPerPage);

  // Function to calculate the number of items that can be displayed
  const calculateContractorsPerPage = () => {
    if (!contractorListRef.current) return contractorsPerPage;

    const containerHeight = contractorListRef.current.clientHeight;
    const availableHeight = containerHeight - CONTRACTOR_SIDEBAR_CONSTANTS.PADDING_BUFFER;
    const calculatedItems = Math.floor(availableHeight / CONTRACTOR_SIDEBAR_CONSTANTS.ITEM_HEIGHT) - 1;
    
    // Ensure at least MIN_ITEMS are displayed
    return Math.max(CONTRACTOR_SIDEBAR_CONSTANTS.MIN_ITEMS, calculatedItems);
  };

  // Hook to monitor size changes
  useEffect(() => {
    const updateContractorsPerPage = () => {
      const newContractorsPerPage = calculateContractorsPerPage();
      if (newContractorsPerPage !== calculatedContractorsPerPage) {
        setCalculatedContractorsPerPage(newContractorsPerPage);
        onContractorsPerPageChange(newContractorsPerPage);
      }
    };

    // Add small delay to ensure DOM has rendered
    const timeoutId = setTimeout(updateContractorsPerPage, CONTRACTOR_SIDEBAR_CONSTANTS.INITIAL_CALCULATION_DELAY);

    // Monitor window resize events
    const handleResize = () => {
      clearTimeout(timeoutId);
      setTimeout(updateContractorsPerPage, CONTRACTOR_SIDEBAR_CONSTANTS.RESIZE_DEBOUNCE_DELAY);
    };

    window.addEventListener('resize', handleResize);
    
    // Monitor sidebar width changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (contractorListRef.current) {
      resizeObserver.observe(contractorListRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [calculatedContractorsPerPage, onContractorsPerPageChange, sidebarWidth]);

  const totalPages = Math.ceil(totalContractors / contractorsPerPage);

  const handlePageChange = (page: number) => {
    onPageChange(Math.max(1, Math.min(page, totalPages)));
  };

  const handleFastPageChange = (jump: number) => {
    const newPage = currentPage + jump * CONTRACTOR_SIDEBAR_CONSTANTS.FAST_PAGE_JUMP;
    onPageChange(Math.max(1, Math.min(newPage, totalPages)));
  };

  return (
    <StyledSidebar ref={ref} elevation={0} sidebarWidth={sidebarWidth}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`, flexShrink: 0 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {CONTRACTOR_SIDEBAR_CONSTANTS.TITLE}
          </Typography>
          <TextField
            size="small"
            placeholder={CONTRACTOR_SIDEBAR_CONSTANTS.SEARCH_PLACEHOLDER}
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: CONTRACTOR_SIDEBAR_CONSTANTS.SEARCH_FIELD.MARGIN_BOTTOM,
              bgcolor: COLORS_CONFIG_PAGE.background,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                '&:focus': {
                  bgcolor: COLORS_CONFIG_PAGE.primary,
                  '& .MuiInputBase-input': {
                    color: COLORS_CONFIG_PAGE.white,
                  },
                },
              },
              borderRadius: CONTRACTOR_SIDEBAR_CONSTANTS.SEARCH_FIELD.BORDER_RADIUS,
              p: CONTRACTOR_SIDEBAR_CONSTANTS.SEARCH_FIELD.PADDING,
            }}
          />
        </Box>

        {/* Contractors List */}
        <Box 
          ref={contractorListRef}
          sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            p: 1, 
            minHeight: 0 // Important: allows flex item to shrink
          }}
        >
          {contractors.map((contractor) => (
            <Box
              key={contractor.userId}
              onClick={() => onContractorSelect(contractor)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                cursor: 'pointer',
                borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
                bgcolor: selectedContractor?.userId === contractor.userId
                  ? COLORS_ADMIN_PAGE.PRIMARY
                  : COLORS_ADMIN_PAGE.LIGHT_GRAY,
                color: selectedContractor?.userId === contractor.userId
                  ? COLORS_ADMIN_PAGE.WHITE
                  : COLORS_ADMIN_PAGE.BLACK,
                mb: 1,
                borderRadius: '4px',
                height: `${CONTRACTOR_SIDEBAR_CONSTANTS.ITEM_HEIGHT}px`, // Fixed height based on constant
                boxSizing: 'border-box',
                '&:hover': {
                  bgcolor: selectedContractor?.userId === contractor.userId
                    ? COLORS_ADMIN_PAGE.PRIMARY
                    : COLORS_ADMIN_PAGE.BACKGROUND,
                },
              }}
            >
              <Avatar 
                src={contractor.profileImageUrl || ''} 
                sx={{ 
                  width: CONTRACTOR_SIDEBAR_CONSTANTS.AVATAR.WIDTH, 
                  height: CONTRACTOR_SIDEBAR_CONSTANTS.AVATAR.HEIGHT, 
                  mr: CONTRACTOR_SIDEBAR_CONSTANTS.AVATAR.MARGIN_RIGHT 
                }} 
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  overflow: 'hidden', 
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap' // Prevent text wrapping
                }}
              >
                {contractor.firstName} {contractor.middleName} {contractor.lastName}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Pagination */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
            borderTop: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
            bgcolor: COLORS_ADMIN_PAGE.WHITE,
            flexShrink: 0,
          }}
        >
          <Box>
            <Button
              variant="contained"
              onClick={() => handleFastPageChange(-1)}
              disabled={currentPage <= CONTRACTOR_SIDEBAR_CONSTANTS.FAST_PAGE_JUMP}
              sx={{ 
                bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
                mr: 0.5, 
                minWidth: CONTRACTOR_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
                px: CONTRACTOR_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
              }}
            >
              <KeyboardDoubleArrowLeft />
            </Button>
            <Button
              variant="contained"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              sx={{ 
                bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
                mr: 1, 
                minWidth: CONTRACTOR_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
                px: CONTRACTOR_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
              }}
            >
              <KeyboardArrowLeft />
            </Button>
          </Box>
          
          <Typography variant="body2" sx={{ color: COLORS_ADMIN_PAGE.TEXT_SECONDARY, mx: 1 }}>
            {currentPage} of {totalPages}
          </Typography>
          
          <Box>
            <Button
              variant="contained"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              sx={{ 
                bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
                ml: 1, 
                minWidth: CONTRACTOR_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
                px: CONTRACTOR_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
              }}
            >
              <KeyboardArrowRight />
            </Button>
            <Button
              variant="contained"
              onClick={() => handleFastPageChange(1)}
              disabled={currentPage >= totalPages - CONTRACTOR_SIDEBAR_CONSTANTS.FAST_PAGE_JUMP + 1}
              sx={{ 
                bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
                ml: 0.5, 
                minWidth: CONTRACTOR_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
                px: CONTRACTOR_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X 
              }}
            >
              <KeyboardDoubleArrowRight />
            </Button>
          </Box>
        </Box>
      </Box>
    </StyledSidebar>
  );
});

ContractorSidebar.displayName = 'ContractorSidebar';

export default ContractorSidebar;